// src/lib/paddle.js
// Lightweight Paddle Billing client for Glow & Grow.
//
// - Overlay checkout on desktop via Paddle.Checkout.open (uses @paddle/paddle-js).
// - Hosted redirect on mobile via a server-created transaction whose
//   `checkout.url` we hand to window.location.assign — the server call goes
//   through the `paddle-billing` Supabase Edge Function so the Paddle
//   Server-side API key stays server-side.

import { initializePaddle } from "@paddle/paddle-js";
import { supabase } from "./supabase";

const ENV = process.env.REACT_APP_PADDLE_ENV || "sandbox";
const CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

export const PRICES = {
  monthly: process.env.REACT_APP_PADDLE_MONTHLY_PRICE_ID,
  yearly: process.env.REACT_APP_PADDLE_YEARLY_PRICE_ID,
};

let paddlePromise = null;

export function isMobile() {
  // Deprecated — kept exported for callers but no longer branches checkout flow.
  // Paddle Overlay works correctly on modern mobile browsers.
  return false;
}

// Opens Paddle's overlay checkout. Works on desktop AND mobile — Paddle.js
// renders the full card form inline. Payment success is ONLY confirmed by
// the webhook flipping `is_premium` server-side (not by any URL param).
export async function openCheckout(plan) {
  const priceId = PRICES[plan];
  if (!priceId) throw new Error(`Unknown plan: ${plan}`);
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");

  const paddle = await getPaddle();
  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: session.user.email ? { email: session.user.email } : undefined,
    customData: { supabase_user_id: session.user.id },
    settings: { displayMode: "overlay", theme: "light" },
  });
  return { mode: "overlay" };
}

// Open the overlay for an existing Paddle transaction (e.g. when the URL
// contains ?_ptxn=txn_... from a return-redirect). Does nothing if Paddle
// hasn't loaded or no transaction id was provided.
export async function openCheckoutForTransaction(transactionId) {
  if (!transactionId) return;
  const paddle = await getPaddle();
  paddle.Checkout.open({
    transactionId,
    settings: { displayMode: "overlay", theme: "light" },
  });
}

// Lazy-init Paddle.js — cached across the app lifetime.
export function getPaddle() {
  if (!CLIENT_TOKEN) {
    return Promise.reject(new Error("Paddle client token not configured"));
  }
  if (!paddlePromise) {
    paddlePromise = initializePaddle({
      environment: ENV,
      token: CLIENT_TOKEN,
      eventCallback: (event) => {
        // Signal real checkout completion to the app via a window event —
        // Premium.jsx listens and starts the webhook-poll for activation.
        // We NEVER grant premium client-side; the webhook is source of truth.
        if (event?.name === "checkout.completed") {
          try {
            window.dispatchEvent(new CustomEvent("gg:checkout-completed", {
              detail: { transactionId: event?.data?.transaction_id }
            }));
          } catch { /* ignore */ }
        }
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[paddle event]", event?.name, event);
        }
      },
    });
  }
  return paddlePromise;
}

async function authHeader() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");
  return { Authorization: `Bearer ${session.access_token}` };
}

// Cancel at end of current billing period via the paddle-billing edge fn.
export async function cancelSubscription() {
  const { Authorization } = await authHeader();
  const r = await fetch(`${SUPABASE_URL}/functions/v1/paddle-billing`, {
    method: "POST",
    headers: { Authorization, "Content-Type": "application/json" },
    body: JSON.stringify({ action: "cancel" }),
  });
  if (!r.ok) {
    const err = await r.text();
    throw new Error(err || `Cancel failed (${r.status})`);
  }
  return r.json();
}

// Undo a scheduled cancellation.
export async function resumeSubscription() {
  const { Authorization } = await authHeader();
  const r = await fetch(`${SUPABASE_URL}/functions/v1/paddle-billing`, {
    method: "POST",
    headers: { Authorization, "Content-Type": "application/json" },
    body: JSON.stringify({ action: "resume" }),
  });
  if (!r.ok) {
    const err = await r.text();
    throw new Error(err || `Resume failed (${r.status})`);
  }
  return r.json();
}
